import LandingHero from "./sections/LandingHero";
import RichText from "./sections/RichText";
import SplitColumns from "./sections/SplitColumns";
import PartnerBand from "./sections/PartnerBand";
import CapabilityCards from "./sections/CapabilityCards";
import ProductsGrid from "./sections/ProductsGrid";
import NetworkSection from "./sections/NetworkSection";
import WhyChooseGrid from "./sections/WhyChooseGrid";
import ComplianceSection from "./sections/ComplianceSection";
import CalloutBand from "./sections/CalloutBand";
import CtaBanner from "./sections/CtaBanner";
import FaqBlock from "./sections/FaqBlock";
// Product-template-only sections
import ProductHero from "./sections/ProductHero";
import AboutSpecTable from "./sections/AboutSpecTable";
import HowItsMade from "./sections/HowItsMade";
import FeaturesChecklist from "./sections/FeaturesChecklist";
import StorageHandling from "./sections/StorageHandling";
import ProductWhyChoose from "./sections/ProductWhyChoose";

/**
 * Maps a section config's `type` to its component. Everything a page shows —
 * including hero, FAQ and CTA — is a section here, so a page includes exactly
 * the sections it needs, in any order, purely from config. Adding a new section
 * type = add one entry here + its component. Unknown types render nothing.
 */
export const SECTION_REGISTRY = {
  // shared
  hero: LandingHero,
  richText: RichText,
  // Two panels, each with its own heading/body (vs richText's single heading).
  splitColumns: SplitColumns,
  partnerBand: PartnerBand,
  industriesGrid: PartnerBand, // same card-band layout, different content
  // Bordered icon+label cards that centre a partial last row (chemical-toll).
  // Visually distinct enough from partnerBand to be its own component.
  capabilityCards: CapabilityCards,
  productsGrid: ProductsGrid,
  network: NetworkSection,
  whyChoose: WhyChooseGrid,
  compliance: ComplianceSection,
  calloutBand: CalloutBand,
  faq: FaqBlock,
  cta: CtaBanner,
  // product template only
  //
  // `productHero` and `productWhyChoose` are separate components rather than
  // variants of `hero`/`whyChoose`: the product hero needs a chip row inside its
  // left column and the why-choose band needs a dark surface, neither of which
  // the supplier components have. Building them as variants would have meant
  // editing components live on all 18 supplier pages; as separate modules with
  // their own stylesheets, those pages cannot regress by construction.
  productHero: ProductHero,
  aboutTable: AboutSpecTable,
  howItsMade: HowItsMade,
  features: FeaturesChecklist,
  storageHandling: StorageHandling,
  productWhyChoose: ProductWhyChoose,
};

/** Render one section config into its component (safe no-op for unknown types). */
export function renderSection(section, index) {
  const Component = SECTION_REGISTRY[section?.type];
  if (!Component) return null;
  return <Component key={`${section.type}-${index}`} {...section} />;
}

/**
 * Sections that paint their own fixed background and so are exempt from the
 * alternation rule below: the hero, the dark CTA banner, and the FAQ block all
 * own their surface rather than taking one from config.
 */
const SURFACE_EXEMPT = new Set([
  "hero",
  "cta",
  "faq",
  // The product hero and its dark why-choose band both hardcode their own
  // background (white / #040919) and take no `surface`, so the alternation check
  // would compare them against a default that is never rendered.
  "productHero",
  "productWhyChoose",
]);

/** `grey` and `light` both read as a pale band, so they don't separate. */
const SURFACE_GROUP = { light: "pale", grey: "pale", dark: "dark" };

/**
 * Dev-only: warn when two adjacent sections land on the same surface group, which
 * makes them read as one continuous band with no visual separation. Config-only
 * rule, so it is checked here rather than in each section component.
 *
 * Deliberately a warning, not a thrown error or an auto-flip: the correct fix is
 * whichever surface Figma specifies for that band, and silently flipping one
 * would hide a genuine config/Figma mismatch.
 */
function warnOnAdjacentSurfaces(sections) {
  let prev = null;
  sections.forEach((s) => {
    if (!s?.type || SURFACE_EXEMPT.has(s.type)) {
      prev = null;
      return;
    }
    const group = SURFACE_GROUP[s.surface] ?? "pale";
    if (prev && prev.group === group) {
      // eslint-disable-next-line no-console
      console.warn(
        `[landing-pages] "${prev.type}" (${prev.surface}) and "${s.type}" (${s.surface}) are adjacent and both render as a ${group} band — they will read as one section. Alternate the surfaces.`,
      );
    }
    prev = { type: s.type, surface: s.surface, group };
  });
}

/** Render an ordered list of section configs. */
export function renderSections(sections = []) {
  if (process.env.NODE_ENV !== "production") {
    warnOnAdjacentSurfaces(sections);
  }
  return sections.map(renderSection);
}
